import React, { useState, useEffect } from 'react';
import apiService from '../services/api';
import '../styles/DashboardContent.css';

const DashboardContent = ({ onRequestPayout }) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchDashboardStats();
  }, []);

  const fetchDashboardStats = async () => {
    try {
      setLoading(true);
      const result = await apiService.dashboard.getStats();
      
      if (result.success) {
        setStats(result.data);
      }
    } catch (err) {
      setError(err.message || 'Failed to fetch dashboard statistics');
      console.error('Dashboard stats error:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const getChangeIcon = (change) => {
    return change >= 0 ? '↗' : '↘';
  };

  const getChangeClass = (change) => {
    return change >= 0 ? 'up' : 'down';
  };

  if (loading) {
    return (
      <div className="dashboard-content">
        <div className="loading-container">
          <div className="loading-spinner-large"></div>
          <p>Loading dashboard statistics...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard-content">
        <div className="error-container">
          <span className="error-icon">⚠️</span>
          <p>{error}</p>
          <button onClick={fetchDashboardStats} className="retry-btn">
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="dashboard-content">
        <div className="no-data-container">
          <p>No data available</p>
        </div>
      </div>
    );
  }

  const statsData = [
    {
      id: 'qr-clicks',
      value: stats.qrCodeClicks.total.toLocaleString(),
      label: 'QR CODE CLICKS',
      change: `${getChangeIcon(stats.qrCodeClicks.weeklyChange)}${Math.abs(stats.qrCodeClicks.weeklyChange)}% this week`,
      trend: getChangeClass(stats.qrCodeClicks.weeklyChange)
    },
    {
      id: 'registered-users',
      value: stats.registeredUsers.total.toLocaleString(),
      label: 'REGISTERED USERS',
      change: `${getChangeIcon(stats.registeredUsers.weeklyChange)}${Math.abs(stats.registeredUsers.weeklyChange)}% this week`,
      trend: getChangeClass(stats.registeredUsers.weeklyChange)
    },
    {
      id: 'completed-profiles',
      value: stats.completedProfiles.total.toLocaleString(),
      label: 'COMPLETED PROFILES',
      change: `${getChangeIcon(stats.completedProfiles.weeklyChange)}${Math.abs(stats.completedProfiles.weeklyChange)}% this week`,
      trend: getChangeClass(stats.completedProfiles.weeklyChange)
    },
    {
      id: 'subscriptions',
      value: stats.subscriptions.total.toLocaleString(),
      label: 'SUBSCRIPTIONS',
      change: `${getChangeIcon(stats.subscriptions.weeklyChange)}${Math.abs(stats.subscriptions.weeklyChange)}% this week`,
      trend: getChangeClass(stats.subscriptions.weeklyChange)
    }
  ];

  return (
    <div className="dashboard-content">
      {/* Credits Card */}
      <div className="credits-card">
        <div className="credits-amount">{formatCurrency(stats.totalEarnings)}</div>
        <div className="credits-label">Available Credits</div>
        <button className="request-payout-btn" onClick={onRequestPayout}>Request Payout</button>
      </div>

      {/* Stats Grid */}
      <div className="stats-grid">
        {statsData.map(stat => (
          <div key={stat.id} className="stat-card">
            <div className="stat-value">{stat.value}</div>
            <div className="stat-label">{stat.label}</div>
            <div className={`stat-change ${stat.trend}`}>
              {stat.change}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default DashboardContent;